import { ThunkDispatch } from 'redux-thunk';
import _get from 'lodash/get';

import setApiAuthHeader, { setApiAuthenticationHeader } from 'utils/setApiAuthenticationHeader';
import notifier from 'utils/notifier';

import fakeApi from 'constants/fakeApi';
import api from 'constants/api';

import { showAnnouncementBar } from 'store/app/announcements/actions';
import { createStatistic } from 'store/app/actions';

import types from './types';
import { setAppInsightsAuthenticatedUser } from 'index';

interface UserModel {
    "UserId": string,
    "DateCreated": string,
    "SessionId": string,
    "UserRole": number,
    "PasswordExpirationDate": string,
    "FirstName": string,
    "LastName": string,
    "Email": string,
    "ContactPerson": string,
    "ContactPhone": string,
    "BatchSubmitEnabled": null,
    "PreFixPsw": number,
    "RiskGroup": null,
    "Token": string,
    "AuthEnabled": string,
    "ClaimEnabled": string,
    "QueryEligibilityEnabled": string,
    "UploadEnabled": string,
    "LastLoginDate": string,
    "ModifiedDate": string
}

export const login = (credentials: any) => (dispatch: ThunkDispatch<any, any, any>, getState: () => any) => {
  if (getState().rootReducer.auth.isPostingLogin) { return Promise.reject(); }

  dispatch({ type: types.LOGIN_REQUEST });

  const params = new URLSearchParams();
  params.append('UserId', credentials.username);
  params.append('password', credentials.password);

  return api.auth.login(params)
    .then((res: UserModel) => {
      let payload = res;

      if (!payload.Token || !payload.SessionId) {
        return Promise.reject(payload);
      } else {

        sessionStorage.setItem('access_token', payload.Token);
        sessionStorage.setItem('refresh_token', payload.SessionId);
        sessionStorage.setItem('firstName', payload.FirstName);
        sessionStorage.setItem('lastName', payload.LastName);
        sessionStorage.setItem('email', payload.Email);
        sessionStorage.setItem('contactPerson', payload.ContactPerson);
        sessionStorage.setItem('contactPhone', payload.ContactPhone);
        sessionStorage.setItem('user_id', payload.UserId);
        sessionStorage.setItem('user_role', payload.UserRole.toString());
        sessionStorage.setItem('ipaGroup', credentials.selectedIPA.includes('STAGING') ? credentials.selectedIPA.replace('_STAGING', '') : credentials.selectedIPAGroup);
        sessionStorage.setItem('ipa', credentials.selectedIPA.includes('STAGING') ? credentials.selectedIPA.replace('_STAGING', '') : credentials.selectedIPA);
        sessionStorage.setItem('claim_enabled', payload.ClaimEnabled);
        sessionStorage.setItem('auth_enabled', payload.AuthEnabled);
        sessionStorage.setItem('modified_date', payload.ModifiedDate);

        localStorage.setItem("print_ipa", credentials.selectedIPA.includes('STAGING') ? credentials.selectedIPA.replace('_STAGING', '') : credentials.selectedIPA);
        setApiAuthHeader(payload.Token);

        setAppInsightsAuthenticatedUser(payload.UserId);

        dispatch(setRoles(credentials.selectedIPAGroup, credentials.selectedIPA));
        dispatch(showAnnouncementBar());

        dispatch(createStatistic(
          'MIGRATION', 
          sessionStorage.getItem('ipaGroup')!, 
          0, 
          'LOGIN SUCCESS', 
          sessionStorage.getItem('user_id')! 
        ));

        return payload;
      }
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Login failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.LOGIN_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const loginWithStoredToken = () => (dispatch: ThunkDispatch<any, any, any>) => {
  if (sessionStorage.getItem('access_token') && sessionStorage.getItem('refresh_token')) {
    setApiAuthHeader(sessionStorage.getItem('access_token'));

    dispatch({
      type: types.UPDATE_TOKEN_IN_STORE,
      payload: {
        access_token: sessionStorage.getItem('access_token'),
        refresh_token: sessionStorage.getItem('refresh_token'),
      },
    });

    dispatch(setRoles(sessionStorage.getItem('ipaGroup'), sessionStorage.getItem('ipa')));

    dispatch(checkMySelf());
  } else {
    sessionStorage.clear();
    dispatch({ type: types.CHECK_ME_FAILURE });
  }
};

export const acceptOrDeclineTerms = (accepted: boolean) => (dispatch: ThunkDispatch<any, any, any>) => {
  dispatch({
    type: accepted ? types.ACCEPTED_TERMS : types.DECLINED_TERMS,
    payload: accepted,
  });
}

export const checkMySelf = () => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {

  // TODO: Need actual authentication API

  dispatch({ type: types.CHECK_ME_REQUEST });

  return fakeApi.auth.check()
    .then((res: any) => {
      if (res?.status === 'unauthenticated') {
        const refresh_token = getState().rootReducer.auth.refresh_token;

        if (refresh_token) {
          return fakeApi.auth.getNewToken()
            .then(response => {
              const access_tokenNew = response?.access_token;
              const refresh_tokenNew = response?.refresh_token;

              if (access_tokenNew && refresh_tokenNew) {
                setApiAuthenticationHeader(access_tokenNew);
                sessionStorage.setItem('access_token', access_tokenNew);
                sessionStorage.setItem('refresh_token', refresh_tokenNew)

                dispatch({
                  type: types.LOGIN_SUCCESS,
                  payload: {
                    access_token: access_tokenNew,
                    refresh_token: refresh_tokenNew,
                  },
                });

                window.location.reload();

                return Promise.resolve();
              } else {
                return Promise.reject(Error('Can not get access token'));
              }
            });
        }

        return Promise.reject(Error('Unauthenticated'));
      }

      const admin = res.user.ROLES.includes(2);
      const manager = res.user.ROLES.includes(4);
      const payload = { ...res, is_admin: admin, is_manager: manager };

      dispatch({
        type: types.CHECK_ME_SUCCESS,
        payload,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Check failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.CHECK_ME_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const getMySelf = () => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {

  dispatch({ type: types.GET_ME_REQUEST });

  return fakeApi.auth.getMySelf()
    .then((res: any) => {
      const admin = res.user.ROLES.includes(2);
      const manager = res.user.ROLES.includes(4);
      const payload = { ...res, is_admin: admin, is_manager: manager };

      dispatch({
        type: types.GET_ME_SUCCESS,
        payload,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.GET_ME_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const logout = () => (dispatch: ThunkDispatch<any, any, any>) => {
  // localStorage.clear();
  sessionStorage.clear();
  setApiAuthHeader();

  dispatch({
    type: types.LOGOUT_SUCCESS,
    payload: {
      msg: 'Logout success!',
    },
  });

  notifier.success('You have been logged out.');

  window.location.href = '/login';
};

export const setRoles = (ipaGroup: string | null, ipa: string | null) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  dispatch({
    type: types.SET_ROLES,
    payload: {
      ipaGroup: ipaGroup,
      ipa: ipa
    },
  });
};

export const sendRegistration = (form: Object) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  if (getState().rootReducer.auth.isRegistering) { return Promise.reject(); }

  dispatch({ type: types.SEND_REGISTRATION_REQUEST });

  return api.auth.sendRegistration(form)
    .then((res: any) => {

      dispatch({
        type: types.SEND_REGISTRATION_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.SEND_REGISTRATION_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};


export const sendRegistrationToCentral = (form: Object) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  if (getState().rootReducer.auth.isRegisteringCentral) { return Promise.reject(); }

  dispatch({ type: types.SEND_REGISTRATION_CENTRAL_REQUEST });

  return api.auth.sendRegistrationToCentral(form)
    .then((res: any) => {

      dispatch({
        type: types.SEND_REGISTRATION_CENTRAL_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.SEND_REGISTRATION_CENTRAL_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const checkCurrentPassword = (pwd: string, userId: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {

  dispatch({ type: types.CHECK_CURRENT_PASSWORD_REQUEST });

  return api.auth.checkCurrentPassword(pwd, userId)
    .then((res: boolean) => {

      dispatch({
        type: types.CHECK_CURRENT_PASSWORD_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.CHECK_CURRENT_PASSWORD_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const checkPasswordHistory = (uid: string, pwd: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {

  dispatch({ type: types.CHECK_PASSWORD_HISTORY_REQUEST });

  return api.auth.checkPasswordHistory(uid, pwd)
    .then((res: boolean) => {

      dispatch({
        type: types.CHECK_PASSWORD_HISTORY_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.CHECK_PASSWORD_HISTORY_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const updatePassword = (input: string, userId: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  if (getState().rootReducer.auth.isUpdatingPass) { return Promise.reject(); }

  dispatch({ type: types.UPDATE_PASSWORD_REQUEST });

  return api.auth.updatePassword(input, userId)
    .then((res: number) => {

      dispatch({
        type: types.UPDATE_PASSWORD_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.UPDATE_PASSWORD_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const resetPassword = (username: string, code: string, password: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  if (getState().rootReducer.auth.isResettingPass) { return Promise.reject(); }

  dispatch({ type: types.RESET_PASSWORD_REQUEST });

  return api.auth.resetPassword(username, code, password)
    .then((res: any) => {

      dispatch({
        type: types.RESET_PASSWORD_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.RESET_PASSWORD_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const forgotPassword = (username: string, portalName: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {

  if (getState().rootReducer.auth.forgotPassword) { return Promise.reject(); }

  dispatch({ type: types.FORGOT_PASSWORD_REQUEST });

  return api.auth.forgotPassword(username, portalName)
    .then((res: number) => {

      dispatch({
        type: types.FORGOT_PASSWORD_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.FORGOT_PASSWORD_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};

export const updateLastLoginDate = (userId: string) => (dispatch: ThunkDispatch<any, any, any>, getState: any) => {
  // if (getState().rootReducer.auth.isUpdatingLastLogin) { return Promise.reject(); }

  dispatch({ type: types.UPDATE_LAST_LOGIN_DATE_REQUEST });

  return api.auth.updateLastLoginDate(userId)
    .then((res: any) => {

      dispatch({
        type: types.UPDATE_LAST_LOGIN_DATE_SUCCESS,
        payload: res,
      });

      return res;
    })
    .catch((err: Error) => {
      const msg = _get(err, 'response.data.msg', 'Get failed!');
      const errors = _get(err, 'response.data.errors', {});
      const payload = { ...err, msg, errors };

      dispatch({
        type: types.UPDATE_LAST_LOGIN_DATE_FAILURE,
        payload,
      });

      return Promise.reject(payload);
    });
};
